import { DictateMic, type DictateOpts } from './DictateMic.js';
import { NoteTextarea } from './NoteTextarea.js';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { markRuns, selectionQuote, type Mark } from '../lib/noteReview.js';
import {
  PHRASE_PACKS,
  dotMatches,
  dotTrigger,
  nextBlank,
  phraseUsed,
  withPhrase,
  type DotItem,
} from '../lib/dotPhrases.js';
import { NOTEPILOT_SUMMARY_MAX } from '@ehr/shared';
import { NP_TONES, restyleText, type NpContext, type NpTone } from '../lib/notepilot.js';

/**
 * The Draft with AI card + the "Adjust note" note field (calendar#46 step 5,
 * PROGRESS_NOTE_A-15 buildNoteField). Built-in drafter only: nothing leaves the browser
 * except, when the practice turns it on, dictation audio, which the browser sends to its
 * own speech service.
 */

/**
 * ehr#478 (PROGRESS_NOTE_10-14-V81 aiCard, Chase 10-17): the rail's Draft with AI card. One box
 * for the session's rough notes (typed, pasted or dictated) and Draft. It replaces the session
 * summary editor and the treatment approach search. The drafter is the built-in one (notepilot.ts):
 * nothing is sent to an AI service. Once drafted, the box folds to "Rough notes" and Regenerate.
 * ehr#521 (Jay 10-18): the card and its button are named Generate Notes (was Draft with AI /
 * Draft with NotePilot).
 */
/** ehr#682 (V131): the in-progress label for AI-generated text. */
export const GENERATING = 'Generating...';
/** The Generate Notes button's text: Generating... while it runs, Regenerate Notes once filled. */
export function generateLabel(busy: boolean, drafted: boolean): string {
  if (busy) return GENERATING;
  return drafted ? 'Regenerate Notes' : 'Generate Notes';
}

/** The rough-notes box's placeholder on every note. */
export const ROUGH_PLACEHOLDER =
  "Type, paste or dictate the session's rough notes. Generate Notes fills the note's empty boxes from them; review each box before signing.";
/**
 * ehr#708 (DISCHARGE_SUMMARY_NOTE_10-30-V5 QA block 17): a discharge summary's, in V5's words. It
 * names no interventions.
 */
export const DISCHARGE_ROUGH_PLACEHOLDER =
  "Type, paste or dictate notes on the course of treatment. AI fills the note's boxes and proposes goal statuses; review each box before signing.";

export function DraftWithAiCard({
  value,
  onChange,
  dictate,
  locked,
  onDraft,
  drafted = false,
  placeholder = ROUGH_PLACEHOLDER,
  onDelete,
}: {
  /**
   * ehr#682 (V141 V83.5): the red button beside Generate once the note holds generated text. It
   * opens the note's Clear note confirm, so the flow is Clear note, then Generate. ehr#866: it
   * reads Clear note (it was "Delete note", but it clears the note and deletes nothing).
   */
  onDelete?: () => void;
  /** The rough notes (plain text). */
  value: string;
  onChange: (text: string) => void;
  dictate: DictateOpts;
  locked: boolean;
  /** Draft from the rough notes; returns the status line to show. */
  onDraft: (roughNotes: string) => string;
  /** The note holds a draft not yet edited. */
  drafted?: boolean;
  /** ehr#708: the rough-notes box's placeholder (a discharge summary has its own). */
  placeholder?: string;
}) {
  const [rough, setRough] = useState(!drafted);
  useEffect(() => {
    if (drafted) setRough(false);
  }, [drafted]);
  const [status, setStatus] = useState('');
  // ehr#682 (V131): "Generating..." shows on the button and the status line while the boxes
  // fill. The built-in generator is quick, so it waits one frame for the label to paint first.
  const [busy, setBusy] = useState(false);
  const draft = () => {
    if (busy) return;
    setBusy(true);
    setStatus(GENERATING);
    const run = () => {
      try {
        setStatus(onDraft(value.trim()));
      } finally {
        setBusy(false);
      }
    };
    if (typeof requestAnimationFrame === 'function')
      requestAnimationFrame(() => setTimeout(run, 0));
    else setTimeout(run, 0);
  };
  return (
    <section className="pn-card pn-np pn-ai" aria-label="Generate Notes">
      <div className="pn-rail-h pn-np-head">
        <h3>Generate Notes</h3>
        {drafted && <span className="pn-tag">Generated</span>}
      </div>
      {rough ? (
        <div className="pn-ta-wrap pn-ai-rough">
          <NoteTextarea
            className="pn-extra-ta"
            value={value}
            maxLength={NOTEPILOT_SUMMARY_MAX}
            readOnly={locked}
            aria-label="Rough notes"
            placeholder={placeholder}
            onText={onChange}
          />
          {!locked && (
            <DictateMic dictate={dictate} label="Rough notes" value={value} onText={onChange} />
          )}
        </div>
      ) : (
        <button
          type="button"
          className="pn-link pn-np-rough"
          aria-expanded={false}
          onClick={() => setRough(true)}
        >
          Rough notes
        </button>
      )}
      <div className="pn-ai-foot">
        <span className="pn-ai-stat" role="status">
          {status}
        </span>
        {!locked && drafted && onDelete && (
          <button type="button" className="btn small danger" onClick={onDelete} disabled={busy}>
            Clear note
          </button>
        )}
        {!locked && (
          <button
            type="button"
            className="btn small primary"
            title="Fill the note's empty boxes from the rough notes; boxes with text are left alone (built-in generator, nothing leaves the browser)"
            onClick={draft}
            disabled={busy}
            aria-busy={busy || undefined}
          >
            {generateLabel(busy, drafted)}
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * One draftable note field: label row with an "Adjust note" link, the textarea, and a
 * tone row (Formal / Clinical / In-depth, plus Undo) that stays hidden until asked for.
 * A field NotePilot drafted is tinted until the clinician edits it.
 */
/**
 * ehr#571 (reopen 10-24, point 4, V141 V92): the highlights behind a box (the words a comment is
 * on). A layer under the textarea with the same box metrics and transparent text, so only the
 * highlight shows through; it follows the box's scroll and size.
 */
function BoxMarks({
  box,
  value,
  marks,
}: {
  box: RefObject<HTMLTextAreaElement | null>;
  value: string;
  marks: Mark[];
}) {
  const layer = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const ta = box.current;
    const el = layer.current;
    if (!ta || !el) return;
    const fit = () => {
      const cs = getComputedStyle(ta);
      Object.assign(el.style, {
        top: `${ta.offsetTop}px`,
        left: `${ta.offsetLeft}px`,
        width: `${ta.offsetWidth}px`,
        height: `${ta.offsetHeight}px`,
        padding: cs.padding,
        borderStyle: 'solid',
        borderColor: 'transparent',
        borderWidth: cs.borderWidth,
        borderRadius: cs.borderRadius,
        font: cs.font,
        letterSpacing: cs.letterSpacing,
        lineHeight: cs.lineHeight,
      });
      el.scrollTop = ta.scrollTop;
    };
    fit();
    const sync = () => {
      el.scrollTop = ta.scrollTop;
    };
    ta.addEventListener('scroll', sync);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    ro?.observe(ta);
    return () => {
      ta.removeEventListener('scroll', sync);
      ro?.disconnect();
    };
  });
  return (
    <div className="tc-box-marks" ref={layer} aria-hidden="true">
      {markRuns(value, marks).map((r, i) =>
        r.cls ? (
          <mark key={i} className={r.cls}>
            {r.text}
          </mark>
        ) : (
          <span key={i}>{r.text}</span>
        ),
      )}
      {'\n'}
    </div>
  );
}

export function NoteField({
  label,
  field,
  hint,
  value,
  drafted,
  className,
  context,
  onEdit,
  onReplace,
  headExtra,
  quick,
  beforeBox,
  afterBox,
  boxLabel,
  showAdjust = true,
  dictate,
  onAccept,
  marks,
  onSelectText,
  missing = false,
}: {
  label: string;
  field: string;
  hint: string;
  value: string;
  drafted: boolean;
  className: string;
  context: () => NpContext;
  /** The clinician typed: the text is theirs now. */
  onEdit: (v: string) => void;
  /** ehr#571 (V141): Accept keeps the generated text as it is, as if the clinician had edited it. */
  onAccept?: () => void;
  /** Adjust / Undo swapped the text in. */
  onReplace: (v: string) => void;
  /** ehr#437: more in the header after Adjust note (the V81 Required tag and fold button). */
  headExtra?: ReactNode;
  /**
   * ehr#437 part 4 (V81 Quick add): the box's phrase pills, "From your notes", and the dot phrases
   * (".name" offers one; Tab goes to the next __ blank). Off when not given.
   */
  quick?: QuickAdd;
  /** ehr#437 part 6: shown between the header and the box (Plan's "Did you: …?" questions). */
  beforeBox?: ReactNode;
  /** ehr#437 part 7: shown right under the box (the copied-text check). */
  afterBox?: ReactNode;
  /** ehr#571 (reopen 10-24, V141): right above the box ("Edit the note" on an edited box). */
  boxLabel?: ReactNode;
  /** ehr#571 (reopen 10-24): the Adjust note link; off while the note is in review (V141). */
  showAdjust?: boolean;
  /** ehr#458 (V81 "the mic on every box"): dictate into this box. */
  dictate?: DictateOpts;
  /** ehr#571 (reopen 10-24, V141): highlighted words in the box (what comments are on). */
  marks?: Mark[];
  /**
   * ehr#571 (reopen 10-24, V141 V92): words selected in the box (null when the selection is gone),
   * with where the pointer was, for the Comment button that follows a selection.
   */
  onSelectText?: (sel: { quote: string; pos: number; x: number; y: number } | null) => void;
  /**
   * ehr#688 (GROUP_NOTE_10-29-V25, V21 22a): the box is empty and sign-off needs it, so it shows red
   * as missing (the box and its title; the group member note only, for now).
   */
  missing?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ta = useRef<HTMLTextAreaElement>(null);
  const [dot, setDot] = useState<{ start: number; q: string; items: DotItem[]; i: number } | null>(
    null,
  );
  const [packsOpen, setPacksOpen] = useState(false);
  const [pack, setPack] = useState<string | null>(null);
  // Where the cursor goes once the new text is in (the first __ blank of what was added).
  const select = useRef<[number, number] | null>(null);
  useEffect(() => {
    const el = ta.current;
    if (!select.current || !el) return;
    const [a, b] = select.current;
    select.current = null;
    el.focus();
    el.setSelectionRange(a, b);
  });
  const checkDot = (el: HTMLTextAreaElement) => {
    if (!quick || el.readOnly || el.selectionStart !== el.selectionEnd) return setDot(null);
    const t = dotTrigger(el.value, el.selectionStart);
    const items = t ? dotMatches(quick.dots, t.q) : [];
    if (!t || !items.length) return setDot(null);
    setDot({
      ...t,
      items,
      i: Math.max(
        0,
        items.findIndex((d) => d.name === t.q),
      ),
    });
  };
  // ehr#910: what the box holds now (the note's copy can be a keystroke or two behind while typing)
  const live = () => ta.current?.value ?? value;
  const insertDot = (d: DotItem) => {
    if (!dot || !quick) return;
    const txt = quick.fill(d.text);
    const now = live();
    const v = now.slice(0, dot.start) + txt + now.slice(dot.start + dot.q.length + 1);
    const b = v.indexOf('__', dot.start);
    const stop = dot.start + txt.length;
    select.current = b >= 0 && b < stop ? [b, b + 2] : [stop, stop];
    setDot(null);
    onEdit(v);
  };
  const addPhrase = (ph: string) => {
    const now = live();
    const kept = now.replace(/\s+$/, '');
    const v = withPhrase(now, ph);
    const b = v.indexOf('__', kept ? kept.length + 1 : 0);
    select.current = b >= 0 ? [b, b + 2] : [v.length, v.length];
    onEdit(v);
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!quick) return;
    if (dot) {
      const n = dot.items.length;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setDot({ ...dot, i: (dot.i + (e.key === 'ArrowDown' ? 1 : n - 1)) % n });
        return;
      }
      if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
        e.preventDefault();
        insertDot(dot.items[dot.i]!);
        return;
      }
      if (e.key === ' ' && dot.items[dot.i]?.name === dot.q) {
        e.preventDefault();
        insertDot(dot.items[dot.i]!);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setDot(null);
      }
      return;
    }
    if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const el = e.currentTarget;
      const b = nextBlank(el.value, el.selectionEnd);
      if (b >= 0) {
        e.preventDefault();
        el.setSelectionRange(b, b + 2);
      }
    }
  };
  // ehr#571 (reopen 10-24, V141 V92): a selection in the box offers a comment on those words
  const pickText = (el: HTMLTextAreaElement, x: number, y: number) => {
    if (!onSelectText) return;
    const q = selectionQuote(el.value, el.selectionStart, el.selectionEnd);
    onSelectText(q ? { ...q, x, y } : null);
  };
  const [prev, setPrev] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const adjust = (tone: NpTone, name: string) => {
    const now = live();
    const text = now.trim();
    if (!text) {
      setStatus('Nothing to adjust yet. Generate or type something first.');
      return;
    }
    setPrev(now);
    onReplace(restyleText(text, tone, field, context()));
    setStatus(`${name} version, built-in restyle.`);
  };
  let hintLine: ReactNode = null;
  if (drafted)
    hintLine = (
      <div className="pn-np-drafted-hint">
        AI text. Read it and make it yours before you submit.
        {onAccept && (
          <button type="button" className="pn-pill add pn-np-accept" onClick={onAccept}>
            Accept
          </button>
        )}
      </div>
    );
  return (
    <div className={missing ? `${className} pn-miss-red` : className}>
      <div className="pn-field-head">
        <label>{label}</label>
        {showAdjust && (
          <button type="button" className="pn-adjust-link" onClick={() => setOpen((o) => !o)}>
            Adjust note
          </button>
        )}
        {headExtra}
      </div>
      {beforeBox}
      {boxLabel}
      <div className={`pn-ta-wrap${marks?.length ? ' tc-has-marks' : ''}`}>
        {marks && marks.length > 0 && <BoxMarks box={ta} value={value} marks={marks} />}
        <NoteTextarea
          ref={ta}
          className={drafted ? 'drafted' : undefined}
          value={value}
          placeholder={hint}
          aria-label={label}
          aria-invalid={missing || undefined}
          onText={onEdit}
          onChange={(e) => checkDot(e.target)}
          onSelect={(e) => checkDot(e.currentTarget)}
          onKeyDown={onKey}
          onBlur={() => setDot(null)}
          onMouseUp={
            onSelectText ? (e) => pickText(e.currentTarget, e.clientX, e.clientY) : undefined
          }
          onKeyUp={
            onSelectText
              ? (e) => {
                  // a selection made with Shift and the arrow keys
                  if (!e.shiftKey && e.key !== 'Shift') return;
                  const r = e.currentTarget.getBoundingClientRect();
                  pickText(e.currentTarget, r.left + r.width / 2, r.top + 8);
                }
              : undefined
          }
        />
        {dictate && <DictateMic dictate={dictate} label={label} value={value} onText={onEdit} />}
        {dot && quick && (
          <div
            className="pn-dotpop"
            role="listbox"
            aria-label="Dot phrases"
            onMouseDown={(e) => e.preventDefault()}
          >
            {dot.items.map((d, k) => (
              <div
                key={d.name}
                role="option"
                aria-selected={k === dot.i}
                className={`pn-dot-opt${k === dot.i ? ' on' : ''}`}
                onClick={() => insertDot(d)}
              >
                <b>.{d.name}</b>
                {d.source === 'mine' && <span className="pn-tag pn-tag-green">Mine</span>}
                <span className="pn-dot-pv">{quick.fill(d.text)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {afterBox}
      {hintLine}
      {quick && (
        <QuickPills
          value={value}
          quick={quick}
          pack={pack}
          packsOpen={packsOpen}
          onPack={(id) => {
            setPack(id);
            setPacksOpen(false);
          }}
          onPacksOpen={() => setPacksOpen((o) => !o)}
          onAdd={addPhrase}
        />
      )}
      {open && showAdjust && (
        <div className="pn-adjust-row">
          {NP_TONES.map(([tone, name]) => (
            <button key={tone} type="button" className="pn-chip" onClick={() => adjust(tone, name)}>
              {name}
            </button>
          ))}
          {prev !== null && (
            <button
              type="button"
              className="pn-chip"
              onClick={() => {
                onReplace(prev);
                setPrev(null);
                setStatus('Previous text restored.');
              }}
            >
              Undo
            </button>
          )}
          <span className="pn-adjust-status">{status}</span>
        </div>
      )}
    </div>
  );
}

/** ehr#437 part 4 (V81 Quick add): what a note box offers. */
export interface QuickAdd {
  /** The box's own phrases (V81 BOX_DEFAULTS). */
  phrases: readonly string[];
  /** "From your notes": your most repeated sentences in this box. */
  mine: string[];
  /** Every dot phrase you can use (yours and the practice's). */
  dots: DotItem[];
  /** Fills {client}, {date}, {length} and {last plan}. */
  fill: (text: string) => string;
  /** ehr#571 (V141 + Add phrase): phrases you added to this box yourself (shown first). */
  added?: readonly string[];
  /** ehr#571: "+ Add phrase" opens the window to add one. */
  onAddPhrase?: () => void;
  /**
   * ehr#571 (case management round, V20): the line "+ Last session's plan" adds (the last signed
   * note's plan, of this kind), on the note's first box only.
   */
  lastPlan?: string;
  /** The box's name, for "Already in <box>." */
  boxLabel?: string;
  /** ehr#724 (ADMIN_PHONE_NOTE_10-30-V7): no "+ Phrases" (the therapy packs) under the box. */
  noPacks?: boolean;
}

const PILLS_SHOWN = 6;
/** The pill rows under a box: From your notes, the box's phrases, a picked pack, + Phrases. */
function QuickPills({
  value,
  quick,
  pack,
  packsOpen,
  onPack,
  onPacksOpen,
  onAdd,
}: {
  value: string;
  quick: QuickAdd;
  pack: string | null;
  packsOpen: boolean;
  onPack: (id: string | null) => void;
  onPacksOpen: () => void;
  onAdd: (phrase: string) => void;
}) {
  const unused = (l: readonly string[]) => l.filter((p) => !phraseUsed(value, p));
  const mine = unused(quick.mine);
  const added = unused(quick.added ?? []).filter((p) => !mine.includes(p));
  const own = unused(quick.phrases).filter((p) => !mine.includes(p) && !added.includes(p));
  const packPhrases = unused(PHRASE_PACKS.find((x) => x.id === pack)?.phrases ?? []);
  // ehr#571 (V20 addOnce): a second click says it's already there
  const [planMsg, setPlanMsg] = useState('');
  useEffect(() => setPlanMsg(''), [value]);
  const pill = (p: string, cls = '') => (
    <button key={p} type="button" className={`pn-pill${cls}`} onClick={() => onAdd(p)}>
      {p}
    </button>
  );
  return (
    <div className="pn-quick">
      {mine.length > 0 && (
        <div className="pn-pills">
          <span className="pn-pills-h">From your notes</span>
          {mine.map((p) => pill(p, ' mine'))}
        </div>
      )}
      <div className="pn-pills">
        {added.map((p) => pill(p, ' added'))}
        {own.slice(0, PILLS_SHOWN).map((p) => pill(p))}
        {packPhrases.map((p) => pill(p))}
        {quick.onAddPhrase && (
          <button type="button" className="pn-pill add" onClick={quick.onAddPhrase}>
            + Add phrase
          </button>
        )}
        {!quick.noPacks && (
          <button
            type="button"
            className="pn-pill add"
            aria-expanded={packsOpen}
            onClick={onPacksOpen}
          >
            + Phrases
          </button>
        )}
        {quick.lastPlan && (
          <button
            type="button"
            className="pn-pill add"
            data-test="last-plan-pill"
            onClick={() => {
              const line = quick.lastPlan!;
              if (value.includes(line)) setPlanMsg(`Already in ${quick.boxLabel ?? 'this box'}.`);
              else onAdd(line);
            }}
          >
            {"+ Last session's plan"}
          </button>
        )}
        {planMsg && (
          <span className="pn-pill-msg" role="status">
            {planMsg}
          </span>
        )}
      </div>
      {packsOpen && (
        <div className="pn-pills" role="group" aria-label="Phrase packs">
          {PHRASE_PACKS.map((pk) => (
            <button
              key={pk.id}
              type="button"
              className="pn-pill pack"
              aria-pressed={pack === pk.id}
              onClick={() => onPack(pack === pk.id ? null : pk.id)}
            >
              {pk.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
